import {
  clampToRange,
  padAndBoundRect,
  parseFiniteRectTuple,
} from "../../../../utils/censorshipGeometry";
import { IMAGE_TOOLS_TOKENS as imageToolsTokens } from "./tokens";

export type TCensorshipStickerPlacement = {
  id: string;
  regionKey: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

type TBbox = [number, number, number, number] | null;

const STICKER_PADDING_RATIO = 0.2;

export const createCensorshipStickerPlacements = ({
  bboxes,
  imageWidth,
  imageHeight,
}: {
  bboxes: Record<string, TBbox>;
  imageWidth: number;
  imageHeight: number;
}): TCensorshipStickerPlacement[] =>
  Object.entries(bboxes).flatMap(([regionKey, bbox], index) => {
    const parsedBbox = parseFiniteRectTuple(bbox, { reorder: true });
    if (!parsedBbox) return [];

    const x1 = clampToRange(parsedBbox.x1, 0, imageWidth);
    const y1 = clampToRange(parsedBbox.y1, 0, imageHeight);
    const x2 = clampToRange(parsedBbox.x2, 0, imageWidth);
    const y2 = clampToRange(parsedBbox.y2, 0, imageHeight);
    if (x2 <= x1 || y2 <= y1) return [];

    const placement = padAndBoundRect({
      rect: {
        left: x1,
        top: y1,
        width: x2 - x1,
        height: y2 - y1,
      },
      bounds: { minX: 0, minY: 0, maxX: imageWidth, maxY: imageHeight },
      paddingRatio: STICKER_PADDING_RATIO,
      minimumPadding: 2,
      minimumWidth: imageToolsTokens.size.stickerMinSide,
      minimumHeight: imageToolsTokens.size.stickerMinSide,
      mode: "preserve-padded-size",
    });
    if (!placement) return [];

    return [
      {
        id: `${regionKey}-${index}`,
        regionKey,
        x: placement.left / imageWidth,
        y: placement.top / imageHeight,
        width: placement.width / imageWidth,
        height: placement.height / imageHeight,
      },
    ];
  });
